import {
  CloseOutlined,
  MessageOutlined,
  CustomerServiceOutlined,
  SafetyOutlined,
  InfoCircleOutlined,
  LogoutOutlined,
  StarOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import { Drawer, Avatar, Typography } from "antd";
import { Collapse, List, Switch } from "antd-mobile";
import { useAppNavigate, TMobileRouterPath } from "../../../hooks/router";
import { routes } from "../../../router/mobile/routes";
import styles from "./styles.module.scss";
import { ConfirmModal } from "../../CustomComponents/ConfirmModal/index.mobile";
import { IAnalyticsController } from "../../../hexagon/adapters/driver/analyticsController";
import { analyticsEventSettingsPaths } from ".";
import { useState } from "react";
import { getOperatingSystem } from "../../../util/common";
import classNames from "classnames";
import { DownOutline, RightOutline } from "antd-mobile-icons";

const { Text } = Typography;
const year = new Date().getFullYear();

export interface IMobileLeftMenu {
  open: boolean;
  onToggle: (e?: { stopPropagation: () => void }) => void;
  onLogout: () => void;
  user?: User;
  userClinic?: IClinic;
  confirmLogout: boolean;
  onCancel: () => void;
  onConfirmLogout: () => void;
  analyticsController: IAnalyticsController;
  circadiaEngineProvider: ICircadiaEngineProvider;
  localBetaFeature: boolean;
  onLocalBetaFeature: (value: boolean) => void;
}

export const MobileLeftMenu: FC<IMobileLeftMenu> = (props) => {
  const {
    open,
    onToggle,
    user,
    userClinic,
    onLogout,
    confirmLogout,
    onCancel,
    onConfirmLogout,
    analyticsController,
    circadiaEngineProvider,
  } = props;

  const navigate = useAppNavigate();
  const storedBiometricValue = localStorage.getItem("isBiometric");
  const isBiometric = storedBiometricValue && JSON.parse(storedBiometricValue);
  const [isSwitchBio, setIsSwitchBio] = useState(isBiometric);
  const isNotAndroid =
    getOperatingSystem({ circadiaEngineProvider }) !== "android";

  const onRouteChange = (route: TMobileRouterPath) => {
    const event: string | undefined = analyticsEventSettingsPaths[route];
    if (event) {
      analyticsController.logEvent(event, {
        email: user?.email,
        selectedFacility: userClinic?.name,
        selectedFacilityId: userClinic?.id,
        cUserId: user?.userID,
      });
    }
    navigate(route, {
      state: {
        previousRoute: "Settings",
      },
    });
    onToggle();
  };

  return (
    <>
      <Drawer
        width={285}
        placement="left"
        closable={false}
        zIndex={1031}
        onClose={onToggle}
        open={open}
        destroyOnClose
        className={styles.drawerContainer}
      >
        <div className={styles.menuHeaderContainer}>
          <h3 id="main-heading">Settings</h3>
          <CloseOutlined
            className={styles.viewIcon}
            onClick={onToggle}
            data-test-id="close-icon"
          />
        </div>
        <div className={styles.userBlock}>
          <Avatar
            className={styles.avatar}
            size={40}
            shape="square"
            data-test-id="avatar"
          >
            {`${user?.firstName?.charAt(0).toUpperCase()}${user?.lastName
              ?.charAt(0)
              .toUpperCase()}`}
          </Avatar>
          <h6
            data-test-id="user-name"
            className={styles.name}
          >{`${user?.firstName} ${user?.lastName}`}</h6>
        </div>
        <div className={`${styles.clinicBlock} mb-4`}>
          <p data-test-id="facility-name" className={styles.facilityName}>
            {userClinic?.name}{" "}
          </p>
          <p
            className={styles.changeClinic}
            onClick={() => onRouteChange(routes.changeFacility.path)}
            id="change-facility"
            data-test-id="change-facility"
          >
            Change
          </p>
        </div>
        <Collapse
          className={`mb-3 ${styles.collapse}`}
          style={{ backgroundColor: "white" }}
          arrow={(active) =>
            active ? (
              <DownOutline className={classNames(styles.menuIcon)} />
            ) : (
              <RightOutline className={classNames(styles.menuIcon)} />
            )
          }
        >
          <Collapse.Panel
            key="1"
            title={
              <h6
                className={styles.menuItemTitle}
                id="user-profile-settings-title"
              >
                User Profile
              </h6>
            }
            data-test-id="user-profile"
            onClick={() =>
              analyticsController.logEvent("Tap on user profile tab", {
                email: user?.email,
                selectedFacility: userClinic?.name,
                selectedFacilityId: userClinic?.id,
                cUserId: user?.userID,
              })
            }
          >
            <List className={styles.profileList}>
              <List.Item
                clickable
                className={styles.textStyle}
                arrow={false}
                onClick={() => onRouteChange(routes.changePassword.path)}
                data-test-id="change-password"
              >
                Change Password
              </List.Item>
              <List.Item
                clickable
                className={styles.textStyle}
                arrow={false}
                onClick={() => onRouteChange(routes.changePin.path)}
                data-test-id="change-pin"
              >
                Change PIN
              </List.Item>
            </List>
          </Collapse.Panel>
        </Collapse>
        <div
          className={`${styles.menuItem} mb-3`}
          onClick={() => onRouteChange(routes.floorSettings.path)}
          id="floor-selection"
        >
          <h6
            className={styles.menuItemTitle}
            id="floor-selection-settings-title"
          >
            Floor/Unit Selection
          </h6>
          <RightOutline className={styles.menuIcon} />
        </div>
        <div
          className={`${styles.menuItem} mb-3`}
          onClick={() => onRouteChange(routes.notificationSettings.path)}
          id="notification-settings"
        >
          <h6
            className={`${styles.menuItemTitle}`}
            id="notification-settings-title"
          >
            Notification Settings
          </h6>
          <RightOutline className={styles.menuIcon} />
        </div>
        {circadiaEngineProvider.isWindowCircadia() && isNotAndroid ? (
          <List.Item
            className={`mb-3 ${styles.bioTitle}`}
            arrow={
              <Switch
                data-test-id="biometric-switch"
                disabled={!circadiaEngineProvider.isWindowCircadia()}
                checked={isSwitchBio}
                onChange={(checked) => {
                  setIsSwitchBio(checked);
                  localStorage.setItem("isBiometric", JSON.stringify(checked));
                  if (checked) {
                    analyticsController.logEvent("Biometric Auth enabled", {
                      email: user?.email,
                      selectedFacility: userClinic?.name,
                      selectedFacilityId: userClinic?.id,
                      cUserId: user?.userID,
                    });
                  } else {
                    analyticsController.logEvent("Biometric Auth disabled", {
                      email: user?.email,
                      selectedFacility: userClinic?.name,
                      selectedFacilityId: userClinic?.id,
                      cUserId: user?.userID,
                    });
                  }
                }}
              />
            }
          >
            <div className="d-flex flex-column">
              <h6
                className={`list-name ${styles.menuItemTitle}`}
                id="biometric-title"
              >
                Biometric Auth
              </h6>
            </div>
          </List.Item>
        ) : (
          <></>
        )}
        <div
          data-test-id="logout"
          className={`${styles.menuItem} mb-3 ${styles.logout}`}
          onClick={onLogout}
        >
          <h6
            className={`${styles.logout} ${styles.LogOutAlignCenter}`}
            id="log-out-title"
          >
            Log Out
          </h6>
          <LogoutOutlined className={styles.logo} />
        </div>
        <div className={styles.footer}>
          <Text className={styles.subText} data-test-id="version">
            Version {APP_VERSION} <br /> All Rights Reserved © Circadia
            Technologies Ltd {year}
          </Text>
        </div>
      </Drawer>
      <ConfirmModal
        subTitle="Are you sure you want to log out?"
        data-test-id="confirmation-modal"
        cancelButtonText="Cancel"
        visible={confirmLogout}
        onCancelClick={onCancel}
        onConfirmClick={onConfirmLogout}
      />
    </>
  );
};
